// @ts-nocheck
/**
 * Shared validation for press/trigger handlers (events and component gestures).
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue, makePassResult } from '../../diagnostics';
import { normalizeEventHandler } from '../../types/eventHandler';
import type { PressHandlerInput, ScriptEventHandlerInput } from '../../types/eventHandler';
import type { SemanticLookups } from './lookups';

export function validatePressHandlerReference(
  handler: PressHandlerInput | ScriptEventHandlerInput,
  context: {
    elementId: string;
    path: string;
    label: string;
    lookups: SemanticLookups;
  },
): ValidationResult {
  let result = makePassResult();
  const normalized = normalizeEventHandler(handler);

  if (!normalized) {
    return addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_event_handler',
      message:
        `${context.label} must specify exactly one handler: kind 'script' with scriptId, ` +
        `kind 'action' with actionId, kind 'animation' with animationId, or legacy { scriptId } / { actionId }.`,
      path: `${context.path}`,
    });
  }

  if (normalized.kind === 'script') {
    if (!context.lookups.scriptById.has(normalized.scriptId)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_script_reference',
        message:
          `${context.label} references script '${normalized.scriptId}' which is not defined `
          + 'in document.scripts or any class library',
        path: `${context.path}.scriptId`,
      });
    }
    return result;
  }

  if (normalized.kind === 'animation') {
    if (!context.lookups.animationIds.has(normalized.animationId)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_animation_reference',
        message:
          `${context.label} references animation '${normalized.animationId}' which is not defined on the template`,
        path: `${context.path}.animationId`,
      });
    }
    return result;
  }

  const actionIds = context.lookups.actionIdsByElement.get(context.elementId);
  if (!actionIds?.has(normalized.actionId)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_action_reference',
      message:
        `${context.label} references action '${normalized.actionId}' which is not defined on element '${context.elementId}'`,
      path: `${context.path}.actionId`,
    });
  }
  return result;
}
